import { createAsyncThunk } from "@reduxjs/toolkit";
import { postLoginService } from "../../Services";
import { alert } from "../../utils/commonFunctions/alert";
import { getStoredUser, syncStoredMerchantIdentity } from "../../utils/auth/storedUser";

/**
 * Helper to get role-specific ID from localStorage user object
 * @param {string} role - "merchant" or "influencer"
 * @returns {{ id: number|null, error: string|null }}
 */
const getRoleId = async (role) => {
    try {
        const user = getStoredUser();
        if (!user) return { id: null, error: "User not found. Please login again." };

        if (role === "merchant") {
            let id = user?.profile?.merchantId;

            if (!id) {
                const response = await postLoginService.get(`/api/merchants`);
                const merchantData = response?.data || response;
                syncStoredMerchantIdentity(merchantData);
                id = merchantData?.id || merchantData?._id || merchantData?.merchantId;
            }

            return id ? { id, error: null } : { id: null, error: "Merchant ID not found. Please contact support." };
        }

        if (role === "influencer") {
            const id = user?.profile?.influencerId;
            return id ? { id, error: null } : { id: null, error: "Influencer ID not found. Please contact support." };
        }

        return { id: null, error: "Invalid role." };
    } catch {
        return { id: null, error: "Failed to read user data." };
    }
};

// Fetch KYC status for merchant or influencer
export const fetchKycStatus = createAsyncThunk(
    "kyc/fetchKycStatus",
    async ({ role, id: explicitId } = {}, { rejectWithValue }) => {
        try {
            let entityId = explicitId;

            if (!entityId) {
                const { id, error } = await getRoleId(role);
                if (error) {
                    alert.error(error);
                    return rejectWithValue(error);
                }
                entityId = id;
            }

            const url = `/api/payouts/${role}/${entityId}/kyc-status`;
            const response = await postLoginService.get(url);
            // console.log("KYC STATUS RESPONSE---->", response);

            // API returns { data: { kyc, stripeAccount, bankDetails } }
            const payload = response?.data || response;

            if (payload?.kyc) {
                return { role, ...payload };
            } else {
                const errorMessage = response?.message || "Failed to fetch KYC status";
                alert.error(errorMessage);
                return rejectWithValue(errorMessage);
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to fetch KYC status";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Fetch KYC onboarding link for merchant or influencer
export const fetchKycLink = createAsyncThunk(
    "kyc/fetchKycLink",
    async ({ role, id: explicitId } = {}, { rejectWithValue }) => {
        try {
            let entityId = explicitId;

            if (!entityId) {
                const { id, error } = await getRoleId(role);
                if (error) {
                    alert.error(error);
                    return rejectWithValue(error);
                }
                entityId = id;
            }

            const url = `/api/payouts/${role}/${entityId}/kyc-link`;
            const response = await postLoginService.get(url);
            // console.log("KYC LINK RESPONSE---->", response);

            // API may return { data: { url, expiresAt } } or { url, expiresAt }
            const payload = response?.data || response;

            if (payload?.url) {
                return payload;
            } else {
                const errorMessage = response?.message || "Failed to generate verification link";
                alert.error(errorMessage);
                return rejectWithValue(errorMessage);
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to generate verification link";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

/**
 * Submit bank account details for merchant or influencer.
 * On success the backend creates a Stripe Connect account internally and
 * returns { requiresOnboarding, onboardingUrl } — the frontend MUST open
 * onboardingUrl if requiresOnboarding === true.
 */
export const saveBankDetails = createAsyncThunk(
    "kyc/saveBankDetails",
    async ({ role, data }, { rejectWithValue }) => {
        try {
            const { id: entityId, error } = await getRoleId(role);
            if (error) {
                alert.error(error);
                return rejectWithValue(error);
            }

            const url = `/api/payouts/${role}/${entityId}/bank-details`;
            const response = await postLoginService.post(url, data);
            // console.log("SAVE BANK DETAILS RESPONSE---->", response);

            // Unwrap potential { data: {...} } envelope
            const payload = response?.data || response;

            if (payload?.requiresOnboarding !== undefined || payload?.last4) {
                // Success — return the full response payload (contains requiresOnboarding, onboardingUrl, last4, etc.)
                // alert.success(response?.message || payload?.message || "Bank details saved successfully!");
                return payload;
            } else {
                const errorMessage = response?.message || "Failed to save bank details";
                alert.error(errorMessage);
                return rejectWithValue(errorMessage);
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to save bank details";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);
